
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plane, CheckCircle, Eye, EyeOff, Lock } from "lucide-react";
import { createOrUpdateUser, createCollection, logDownloadAttempt } from "@/services/supabase/dataService";
import { toast } from "@/hooks/use-toast";
interface DownloadDetailsModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (details: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }) => void;
  isSubmitting?: boolean;
}

export const DownloadDetailsModal: React.FC<DownloadDetailsModalProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting = false
}) => {
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmissionComplete, setIsSubmissionComplete] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState(0);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [isPasswordInfoExpanded, setIsPasswordInfoExpanded] = useState(false);

  function isValidEmail(val: string) {
    return /^[\w\.-]+@[\w\.-]+\.\w+$/.test(val);
  }

  const handleSubmit = () => {
    if (!firstName.trim()) {
      setError("First name is required.");
      return;
    }
    if (!lastName.trim()) {
      setError("Last name is required.");
      return;
    }
    if (!email) {
      setError("Email is required.");
      return;
    }
    if (!isValidEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }
    setError(null);

    // Original onSubmit call with password added
    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email,
      password
    });

    // Set submission complete flag to show confirmation
    setIsSubmissionComplete(true);
  };

  // Reset form and state when modal is closed
  useEffect(() => {
    if (!open) {
      setFirstName("");
      setLastName("");
      setEmail("");
      setPassword("");
      setShowPassword(false);
      setError(null);
      setIsSubmissionComplete(false);
      setCurrentStep(0);
      setIsPasswordInfoExpanded(false);
    }
  }, [open]);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const togglePasswordInfo = () => {
    setIsPasswordInfoExpanded(!isPasswordInfoExpanded);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-gradient-to-br from-[#131A2A] via-[#1A1F2C] to-[#1E2538] border border-white/5 shadow-2xl max-w-md w-full p-6 overflow-hidden backdrop-blur-xl">
        {!isSubmissionComplete ? (
          <div className="relative">
            <DialogHeader className="space-y-3 text-left mb-6">
              <div className="relative">
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-r from-[#33C3F0]/20 to-purple-500/20 rounded-full blur-3xl"></div>
                <h2 className="text-3xl font-bold text-white tracking-tight inline-block animate-fade-in relative">
                  <span className="bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                    Download
                  </span>{" "}
                  <span className="bg-gradient-to-r from-[#33C3F0] to-[#5c6bd2] bg-clip-text text-transparent">
                    your videos!
                  </span>
                </h2>
                <p className="text-sm text-gray-400 font-normal mt-2 animate-fade-in delay-100">
                  Enter your details below and we'll send your videos!
                </p>
              </div>
            </DialogHeader>

            <div className="relative space-y-5">
              {/* Animated background elements */}
              <div className="absolute -left-20 top-20 w-40 h-40 bg-[#33C3F0]/5 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute -right-10 bottom-10 w-28 h-28 bg-purple-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
              
              <div className="space-y-4 relative">
                <div className="group">
                  <Label className="block text-sm font-medium text-gray-300 mb-1.5 group-focus-within:text-[#33C3F0] transition-colors">
                    First Name
                  </Label>
                  <div className="relative">
                    <Input 
                      type="text" 
                      value={firstName} 
                      onChange={e => setFirstName(e.target.value)}
                      placeholder="John" 
                      autoFocus 
                      disabled={isSubmitting}
                      className="bg-white/5 backdrop-blur-sm border-gray-700/30 text-white rounded-lg placeholder:text-gray-500 focus:border-[#33C3F0] transition-all duration-300 focus:ring-[#33C3F0]/20 focus:ring-offset-0"
                    />
                    <div className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${firstName ? 'opacity-100' : 'opacity-0'}`}>
                      <div className="w-1.5 h-1.5 rounded-full bg-[#33C3F0] animate-pulse"></div>
                    </div>
                  </div>
                </div>

                <div className="group">
                  <Label className="block text-sm font-medium text-gray-300 mb-1.5 group-focus-within:text-[#33C3F0] transition-colors">
                    Last Name
                  </Label>
                  <div className="relative">
                    <Input 
                      type="text" 
                      value={lastName} 
                      onChange={e => setLastName(e.target.value)}
                      placeholder="Doe" 
                      disabled={isSubmitting}
                      className="bg-white/5 backdrop-blur-sm border-gray-700/30 text-white rounded-lg placeholder:text-gray-500 focus:border-[#33C3F0] transition-all duration-300 focus:ring-[#33C3F0]/20 focus:ring-offset-0"
                    />
                    <div className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${lastName ? 'opacity-100' : 'opacity-0'}`}>
                      <div className="w-1.5 h-1.5 rounded-full bg-[#33C3F0] animate-pulse"></div>
                    </div>
                  </div>
                </div>

                <div className="group">
                  <Label className="block text-sm font-medium text-gray-300 mb-1.5 group-focus-within:text-[#33C3F0] transition-colors">
                    Email address
                  </Label>
                  <div className="relative">
                    <Input 
                      type="email" 
                      value={email} 
                      onChange={e => setEmail(e.target.value)}
                      placeholder="john@example.com" 
                      disabled={isSubmitting}
                      className="bg-white/5 backdrop-blur-sm border-gray-700/30 text-white rounded-lg placeholder:text-gray-500 focus:border-[#33C3F0] transition-all duration-300 focus:ring-[#33C3F0]/20 focus:ring-offset-0"
                    />
                    <div className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${email ? 'opacity-100' : 'opacity-0'}`}>
                      <div className="w-1.5 h-1.5 rounded-full bg-[#33C3F0] animate-pulse"></div>
                    </div>
                  </div>
                </div>

                <div className="group">
                  <Label className="block text-sm font-medium text-gray-300 mb-1.5 group-focus-within:text-[#33C3F0] transition-colors">
                    Password
                  </Label>
                  <div className="relative">
                    <Input 
                      type={showPassword ? "text" : "password"} 
                      value={password} 
                      onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••" 
                      disabled={isSubmitting}
                      className="bg-white/5 backdrop-blur-sm border-gray-700/30 text-white rounded-lg placeholder:text-gray-500 focus:border-[#33C3F0] pr-10 transition-all duration-300 focus:ring-[#33C3F0]/20 focus:ring-offset-0"
                    />
                    <button 
                      type="button" 
                      onClick={toggleShowPassword} 
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors" 
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  
                  {/* New interactive password info box */}
                  <div 
                    className={`mt-2.5 overflow-hidden transition-all duration-500 ease-in-out ${isPasswordInfoExpanded ? 'max-h-72' : 'max-h-14'}`}
                    onClick={togglePasswordInfo}
                  >
                    <div className={`relative group rounded-xl cursor-pointer transform transition-all duration-500 ${isPasswordInfoExpanded ? 'bg-gradient-to-r from-blue-600/20 to-purple-600/20 scale-[1.02]' : 'bg-blue-500/10 hover:scale-[1.01]'} border ${isPasswordInfoExpanded ? 'border-blue-500/40' : 'border-blue-500/20'}`}>
                      
                      {/* Top section that's always visible */}
                      <div className="px-3.5 py-2.5 flex items-center gap-2">
                        <div className={`text-[#33C3F0] p-1 rounded-full ${isPasswordInfoExpanded ? 'bg-blue-500/20' : ''} transition-all duration-300`}>
                          <Lock className={`h-4 w-4 transition-all duration-500 ${isPasswordInfoExpanded ? 'rotate-0' : '-rotate-12'}`} />
                        </div>
                        
                        <div className="flex-1">
                          <span className={`text-sm font-medium ${isPasswordInfoExpanded ? 'text-[#33C3F0]' : 'text-blue-400'}`}>
                            Create your password to unlock benefits
                          </span>
                        </div>
                        
                        <div className={`text-gray-400 transform transition-transform duration-500 ${isPasswordInfoExpanded ? 'rotate-180' : 'rotate-0'}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m6 9 6 6 6-6"/>
                          </svg>
                        </div>
                      </div>

                      {/* Expanded content with benefits */}
                      <div className={`px-4 pb-3.5 pt-1 space-y-3 transition-all duration-500 ${isPasswordInfoExpanded ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="h-px bg-gradient-to-r from-blue-500/5 via-blue-500/20 to-blue-500/5"></div>
                        
                        <div className="grid gap-2.5">
                          <div className="flex items-center gap-2.5">
                            <div className="w-5 h-5 rounded-full bg-[#33C3F0]/10 flex items-center justify-center">
                              <CheckCircle className="h-3.5 w-3.5 text-[#33C3F0]" />
                            </div>
                            <span className="text-xs text-gray-300">Access your download history anytime</span>
                          </div>
                          
                          <div className="flex items-center gap-2.5">
                            <div className="w-5 h-5 rounded-full bg-[#33C3F0]/10 flex items-center justify-center">
                              <CheckCircle className="h-3.5 w-3.5 text-[#33C3F0]" />
                            </div>
                            <span className="text-xs text-gray-300">Save multiple collections in one place</span>
                          </div>
                          
                          <div className="flex items-center gap-2.5">
                            <div className="w-5 h-5 rounded-full bg-[#33C3F0]/10 flex items-center justify-center">
                              <CheckCircle className="h-3.5 w-3.5 text-[#33C3F0]" />
                            </div>
                            <span className="text-xs text-gray-300">Faster downloads with saved preferences</span>
                          </div>
                        </div>
                        
                        <div className="pt-1">
                          <div className="text-center text-[10px] text-blue-300/60">Tap anywhere on this box to collapse</div>
                        </div>
                      </div>
                      
                      {/* Animated pulse effect in the background */}
                      <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                        <div className={`absolute -inset-0.5 rounded-xl bg-gradient-to-r from-[#33C3F0]/0 via-[#33C3F0]/30 to-[#5c6bd2]/0 opacity-0 ${isPasswordInfoExpanded ? 'animate-pulse opacity-100' : 'group-hover:opacity-50'} transition-opacity duration-300`}></div>
                      </div>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="px-3 py-2 rounded-md bg-red-500/10 border border-red-500/20 transform animate-bounce">
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}

                <div className="pt-3">
                  <Button 
                    onClick={handleSubmit} 
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#33C3F0] to-[#5c6bd2] hover:from-[#33C3F0] hover:to-[#4e5bb8] text-white font-medium h-12 rounded-lg shadow-lg shadow-[#33C3F0]/20 transform hover:translate-y-[-2px] transition-all duration-300"
                  >
                    <div className="absolute inset-0 rounded-lg overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full hover:animate-[shimmer_1.5s_infinite]"></div>
                    </div>
                    <Plane className="mr-2 h-5 w-5" />
                    {isSubmitting ? (
                      <span className="flex items-center">
                        Processing
                        <span className="ml-1 inline-flex space-x-1">
                          <span className="animate-ping delay-100 h-1 w-1 rounded-full bg-white"></span>
                          <span className="animate-ping delay-200 h-1 w-1 rounded-full bg-white"></span>
                          <span className="animate-ping delay-300 h-1 w-1 rounded-full bg-white"></span>
                        </span>
                      </span>
                    ) : "Send Me My Collection"}
                  </Button>

                  <div className="mt-5 space-y-2.5">
                    <p className="text-xs text-center text-gray-500 backdrop-blur-md px-3 py-2 rounded-lg bg-gray-800/20 border border-gray-700/20">
                      Your download link will be sent to your email within the next 5-10 minutes.
                    </p>
                    <p className="text-xs text-center text-gray-500 backdrop-blur-md px-3 py-2 rounded-lg bg-gray-800/20 border border-gray-700/20">
                      Note: Videos will be deleted after 48 hours for privacy reasons.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-6 text-center relative">
            <div className="absolute inset-0 -z-10">
              <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20">
                <div className="absolute -top-40 -left-40 w-80 h-80 bg-gradient-to-r from-[#33C3F0] to-blue-600 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-gradient-to-r from-purple-600 to-[#33C3F0] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
              </div>
            </div>
            
            <div className="flex justify-center mb-6 relative">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full bg-[#33C3F0]/10 animate-ping"></div>
              <div className="relative">
                <CheckCircle className="h-20 w-20 text-[#33C3F0] animate-[bounce_1.5s_ease-in-out_infinite] drop-shadow-[0_0_8px_rgba(51,195,240,0.5)]" />
              </div>
            </div>
            
            <h2 className="text-3xl font-bold text-white tracking-tight mb-3">
              <span className="bg-gradient-to-r from-[#33C3F0] to-[#5c6bd2] bg-clip-text text-transparent">
                Request Sent!
              </span>
            </h2>
            
            <p className="text-gray-300 mb-8 max-w-xs mx-auto">
              Your download link will be sent to your email within the next 5-10 minutes. Please check your inbox.
            </p>
            
            <Button 
              onClick={onClose} 
              className="bg-gradient-to-r from-[#33C3F0] to-[#5c6bd2] hover:from-[#33C3F0] hover:to-[#4e5bb8] text-white font-medium px-6 py-2.5 rounded-lg shadow-lg shadow-[#33C3F0]/20 transform hover:translate-y-[-2px] transition-all duration-300"
            >
              <div className="absolute inset-0 rounded-lg overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full hover:animate-[shimmer_1.5s_infinite]"></div>
              </div>
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
